import React, { useEffect, useState } from 'react';
import { Col } from "react-bootstrap";
import { getLanguageColor, formatRelativeTime } from '../utils/githubService';

export const ProjectCard = ({
  title,
  description,
  tech,
  onClick,
  stars,
  forks,
  language,
  lastUpdated,
  githubUrl,
  homepage,
  isLive,
  thumbnail
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const getGithubLink = (desc) => {
    const match = desc.match(/href="(https:\/\/github\.com\/[^"]+)"/);
    return match ? match[1] : null;
  };

  const getLiveLink = (desc) => {
    const match = desc.match(/href="(https:\/\/(?!github\.com)[^"]+)"/);
    return match ? match[1] : null;
  };

  const githubLink = getGithubLink(description);
  const liveLink = getLiveLink(description);
  const actualLiveUrl = homepage || liveLink;
  
  // Curated Unsplash photo IDs mapped to project categories
  const getCuratedImage = (title) => {
    const t = title.toLowerCase();
    // School / Education
    if (t.includes('school') || t.includes('student'))
      return 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=600&h=400&fit=crop';
    // Health / Medical
    if (t.includes('health') || t.includes('hospital') || t.includes('care'))
      return 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=600&h=400&fit=crop';
    // E-commerce / Shopping / Buy-Sell
    if (t.includes('buy') || t.includes('sell') || t.includes('nexus') || t.includes('buying'))
      return 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=600&h=400&fit=crop';
    // Spare Parts / Automotive
    if (t.includes('spare') || t.includes('part') || t.includes('auto'))
      return 'https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=600&h=400&fit=crop';
    // Photography / Event
    if (t.includes('photography') || t.includes('event'))
      return 'https://images.unsplash.com/photo-1452587925148-ce544e77e70d?w=600&h=400&fit=crop';
    // AI / Chatbot
    if (t.includes('ai') || t.includes('bot') || t.includes('chat'))
      return 'https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=600&h=400&fit=crop';
    // Social Media / Facebook Clone
    if (t.includes('facebook') || t.includes('social') || t.includes('skill swap'))
      return 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=600&h=400&fit=crop';
    // Math / Quiz / Game
    if (t.includes('math') || t.includes('quiz') || t.includes('game'))
      return 'https://images.unsplash.com/photo-1509228468518-180dd4864904?w=600&h=400&fit=crop';
    // Real-time / Socket
    if (t.includes('real-time') || t.includes('realtime') || t.includes('socket'))
      return 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=600&h=400&fit=crop';
    // Default — generic coding / tech
    return `https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600&h=400&fit=crop`;
  };

  const fallbackImage = getCuratedImage(title);
  const screenshotImage = actualLiveUrl
    ? `https://s0.wp.com/mshots/v1/${encodeURIComponent(actualLiveUrl)}?w=600`
    : null;
  const projectImage = thumbnail || screenshotImage || fallbackImage;
  
  const [thumbSrc, setThumbSrc] = useState(projectImage);

  useEffect(() => {
    setThumbSrc(projectImage);
  }, [projectImage]);

  return (
    <Col size={12} sm={6} md={4}>
      <div
        style={{
          background: isHovered
            ? 'rgba(255, 255, 255, 0.04)'
            : 'rgba(255, 255, 255, 0.02)',
          border: `1px solid ${isHovered ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.06)'}`,
          borderRadius: '20px',
          padding: '28px',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '480px',
          height: isExpanded ? 'auto' : '480px',
          boxShadow: isHovered
            ? '0 20px 60px rgba(6, 182, 212, 0.12), 0 0 0 1px rgba(6, 182, 212, 0.1)'
            : '0 4px 24px rgba(0, 0, 0, 0.2)',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onClick}
      >
        {/* Top gradient line */}
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, #06b6d4, #8b5cf6, #ec4899)',
          opacity: isHovered ? 1 : 0.3,
          transition: 'opacity 0.3s ease',
        }} />

        {/* Project number badge */}
        <div style={{
          position: 'absolute',
          top: '16px', right: '16px',
          width: '36px', height: '36px',
          borderRadius: '50%',
          background: isHovered 
            ? 'linear-gradient(135deg, #06b6d4, #8b5cf6)' 
            : 'rgba(255, 255, 255, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.3s ease',
          zIndex: 10,
        }}>
          {actualLiveUrl ? (
            <span style={{ fontSize: '14px' }}>🔗</span>
          ) : (
            <span style={{ fontSize: '14px' }}>📂</span>
          )}
        </div>

        {/* Project Thumbnail */}
        <div style={{
          width: 'calc(100% + 56px)',
          height: '180px',
          margin: '-28px -28px 20px -28px',
          overflow: 'hidden',
          position: 'relative',
          background: 'rgba(0, 0, 0, 0.2)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        }}>
          <img 
            src={thumbSrc}
            alt={`${title} preview`}
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: isHovered ? 1 : 0.7,
              transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: isHovered ? 'scale(1.1)' : 'scale(1)',
            }}
            onError={() => {
              if (thumbSrc !== fallbackImage) {
                setThumbSrc(fallbackImage);
              }
            }}
          />
          <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: isHovered 
              ? 'linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.4) 100%)'
              : 'linear-gradient(to bottom, transparent 30%, rgba(0,0,0,0.6) 100%)',
            pointerEvents: 'none',
            transition: 'all 0.3s ease',
          }} />
        </div>

        {/* Title */}
        <h4 style={{
          color: isHovered ? '#fff' : '#f0f0f0',
          fontSize: '20px',
          fontWeight: '700',
          marginBottom: '12px',
          lineHeight: '1.3',
          paddingRight: '48px',
          transition: 'color 0.3s ease',
        }}>
          {title}
        </h4>

        {/* Description */}
        <div style={{ marginBottom: '16px', flex: '0 0 auto' }}>
          <p style={{
            color: isHovered? '#c0c0c8' : '#a0a0a8',
            fontSize: '14px',
            lineHeight: '1.7',
            marginBottom: description.replace(/<[^>]+>/g, '').length > 120 ? '8px' : '0',
            transition: 'color 0.3s ease',
          }}>
            {(() => {
              const cleanDesc = description.replace(/<[^>]+>/g, '');
              const limit = 100;
              if (cleanDesc.length <= limit) return cleanDesc;
              if (isExpanded) return cleanDesc;
              return cleanDesc.substring(0, limit) + '...';
            })()}
          </p>
          {description.replace(/<[^>]+>/g, '').length > 100 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#06b6d4',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                padding: '0',
                transition: 'color 0.2s ease',
              }}
              onMouseEnter={(e) => e.target.style.color = '#8b5cf6'}
              onMouseLeave={(e) => e.target.style.color = '#06b6d4'}
            >
              {isExpanded ? '← Less' : 'More →'}
            </button>
          )}
        </div>

        {/* GitHub Stats */}
        {isLive && (stars !== undefined || forks !== undefined || language) && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            marginBottom: '14px',
            paddingBottom: '14px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          }}>
            {stars !== undefined && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                color: '#a0a0a8', fontSize: '12px', fontWeight: '600',
              }}>
                <span style={{ fontSize: '14px' }}>⭐</span>
                <span>{stars}</span>
              </div>
            )}
            {forks !== undefined && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                color: '#a0a0a8', fontSize: '12px', fontWeight: '600',
              }}>
                <span style={{ fontSize: '14px' }}>🍴</span>
                <span>{forks}</span>
              </div>
            )}
            {language && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                background: 'rgba(255, 255, 255, 0.04)',
                padding: '3px 10px', borderRadius: '12px',
                fontSize: '11px', fontWeight: '600',
              }}>
                <span style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: getLanguageColor(language),
                }} />
                <span style={{ color: '#a0a0a8' }}>{language}</span>
              </div>
            )}
            {lastUpdated && (
              <div style={{
                marginLeft: 'auto', color: '#6b6b75',
                fontSize: '11px', fontStyle: 'italic',
              }}>
                {formatRelativeTime(lastUpdated)}
              </div>
            )}
          </div>
        )}

        {/* Tech Tags */}
        {tech && tech.length > 0 && (
          <div style={{
            display: 'flex', flexWrap: 'wrap',
            gap: '6px', marginBottom: '16px',
          }}>
            {tech.map((technology, idx) => (
              <span key={idx} style={{
                background: 'rgba(6, 182, 212, 0.08)',
                border: '1px solid rgba(6, 182, 212, 0.15)',
                color: '#06b6d4',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: '600',
                letterSpacing: '0.3px',
              }}>
                {technology}
              </span>
            ))}
          </div>
        )}

        {/* Buttons */}
        <div style={{
          display: 'flex', gap: '10px', marginTop: 'auto', paddingTop: '12px',
        }}>
          {(githubLink || githubUrl) && (
            <a
              href={githubUrl || githubLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                flex: 1,
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                color: '#a0a0a8',
                padding: '10px 16px',
                borderRadius: '12px',
                fontSize: '13px',
                fontWeight: '600',
                textDecoration: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                e.currentTarget.style.color = '#f0f0f0';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
                e.currentTarget.style.color = '#a0a0a8';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
              }}
            >
              <span>Code</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>
          )}
          {(liveLink || homepage) && (
            <a
              href={homepage || liveLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                flex: 1,
                background: 'linear-gradient(135deg, #06b6d4, #8b5cf6)',
                border: 'none',
                color: '#fff',
                padding: '10px 16px',
                borderRadius: '12px',
                fontSize: '13px',
                fontWeight: '600',
                textDecoration: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 16px rgba(6, 182, 212, 0.2)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 24px rgba(6, 182, 212, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(6, 182, 212, 0.2)';
              }}
            >
              <span>Live Demo</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
            </a>
          )}
        </div>
      </div>
    </Col>
  );
};
